import React, { useEffect, useMemo, useState } from "react";

// Each line is an array of { t: text, c: tailwind color class } segments
const CODE_LINES = [
  [{ t: "$ ", c: "text-neon-yellow" }, { t: "vonmal deploy --agent", c: "text-white/90" }],
  [{ t: "import ", c: "text-neon-magenta" }, { t: "{ Agent } ", c: "text-white/85" }, { t: "from ", c: "text-neon-magenta" }, { t: '"@vonmal/engine"', c: "text-neon-yellow" }, { t: ";", c: "text-white/50" }],
  [],
  [{ t: "const ", c: "text-neon-magenta" }, { t: "support ", c: "text-neon-cyan" }, { t: "= ", c: "text-white/50" }, { t: "new ", c: "text-neon-magenta" }, { t: "Agent", c: "text-neon-cyan" }, { t: "({", c: "text-white/50" }],
  [{ t: "  model", c: "text-white/85" }, { t: ": ", c: "text-white/50" }, { t: '"gemini-3-flash"', c: "text-neon-yellow" }, { t: ",", c: "text-white/50" }],
  [{ t: "  tools", c: "text-white/85" }, { t: ": [", c: "text-white/50" }, { t: "shopify", c: "text-neon-cyan" }, { t: ", ", c: "text-white/50" }, { t: "gmail", c: "text-neon-cyan" }, { t: ", ", c: "text-white/50" }, { t: "crm", c: "text-neon-cyan" }, { t: "],", c: "text-white/50" }],
  [{ t: "  autonomy", c: "text-white/85" }, { t: ": ", c: "text-white/50" }, { t: '"full"', c: "text-neon-yellow" }, { t: ",", c: "text-white/50" }],
  [{ t: "});", c: "text-white/50" }],
  [],
  [{ t: "await ", c: "text-neon-magenta" }, { t: "support", c: "text-neon-cyan" }, { t: ".", c: "text-white/50" }, { t: "ship", c: "text-neon-cyan" }, { t: "();  ", c: "text-white/50" }, { t: "// live in 7 days", c: "text-white/35" }],
  [],
  [{ t: "✓ deployed → ", c: "text-neon-cyan" }, { t: "production", c: "text-white/90" }, { t: "  [2.4s]", c: "text-white/35" }],
];

export default function TerminalWindow() {
  const flat = useMemo(() => {
    const chars = [];
    CODE_LINES.forEach((line, li) => {
      line.forEach((seg, si) => {
        for (let i = 0; i < seg.t.length; i++) chars.push({ li, si });
      });
      chars.push({ li, si: -1 }); // newline marker
    });
    return chars;
  }, []);

  const [count, setCount] = useState(0);

  useEffect(() => {
    let i = 0;
    let timer;
    const step = () => {
      if (i >= flat.length) {
        timer = setTimeout(() => {
          i = 0;
          setCount(0);
          timer = setTimeout(step, 500);
        }, 3500);
        return;
      }
      i += 1;
      setCount(i);
      timer = setTimeout(step, 18 + Math.random() * 40);
    };
    timer = setTimeout(step, 600);
    return () => clearTimeout(timer);
  }, [flat.length]);

  // Build visible segments from count
  const visible = useMemo(() => {
    const out = CODE_LINES.map(() => []);
    let consumed = 0;
    for (let li = 0; li < CODE_LINES.length; li++) {
      for (let si = 0; si < CODE_LINES[li].length; si++) {
        const seg = CODE_LINES[li][si];
        const remaining = count - consumed;
        if (remaining <= 0) return out;
        const take = Math.min(seg.t.length, remaining);
        out[li].push({ ...seg, t: seg.t.slice(0, take) });
        consumed += take;
      }
      consumed += 1; // newline
      if (consumed > count) return out;
    }
    return out;
  }, [count]);

  const doneLines = visible.filter((l, i) => l.length > 0 || i === 0).length;

  return (
    <div
      data-testid="terminal-window"
      className="rounded-2xl border border-white/15 bg-white/[0.04] backdrop-blur-2xl shadow-[0_0_60px_rgba(0,255,156,0.12)] overflow-hidden"
    >
      {/* Title bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-white/10 bg-white/[0.03]">
        <span className="w-3 h-3 rounded-full bg-[#ff5f57]" />
        <span className="w-3 h-3 rounded-full bg-[#febc2e]" />
        <span className="w-3 h-3 rounded-full bg-[#28c840]" />
        <span className="ml-3 font-mono text-[11px] text-white/40 tracking-wider">vonmal — engine.js</span>
      </div>
      {/* Code body */}
      <div className="p-5 font-mono text-[12.5px] sm:text-[13px] leading-[1.7] min-h-[280px]">
        {visible.map((line, li) => (
          <div key={li} className="whitespace-pre">
            <span className="text-white/20 select-none mr-4">{String(li + 1).padStart(2, "0")}</span>
            {line.map((seg, si) => (
              <span key={si} className={seg.c}>{seg.t}</span>
            ))}
            {li === doneLines - 1 && count < flat.length && (
              <span className="inline-block w-[7px] h-[15px] bg-neon-cyan align-middle animate-pulse ml-0.5" />
            )}
          </div>
        ))}
        {count >= flat.length && (
          <div className="whitespace-pre">
            <span className="text-white/20 select-none mr-4">{String(CODE_LINES.length + 1).padStart(2, "0")}</span>
            <span className="inline-block w-[7px] h-[15px] bg-neon-cyan align-middle animate-pulse" />
          </div>
        )}
      </div>
    </div>
  );
}
